import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import {
   PopWrap,
   SearchBar,
   SearchInput,
   SectionBlock,
   SectionTitle,
   OptionRow,
   HiddenCheckbox,
   Box,
   FooterBar,
} from '../styles/FilterDropdownStyle';
import type { FilterDropdownProps, FilterState } from '../../types/FilterStateInterface';



const groups: { key: keyof FilterState; title: string; options: string[] }[] = [
   { key: 'types', title: 'Transaction Type', options: ['Crypto', 'Giftcard', 'Wallet Top-up', 'Withdrawal'] },
   { key: 'statuses', title: 'Status', options: ['Pending', 'Completed', 'Failed'] },
   { key: 'ranges', title: 'Date Range', options: ['Today', 'This Week', 'This Month'] },
];


const FilterDropdown: React.FC<FilterDropdownProps> = ({ open, onClose, value, onChange, anchorRef }) => {
   const wrapRef = useRef<HTMLDivElement | null>(null);
   const [query, setQuery] = useState('');

   useEffect(() => {
      function handle(e: MouseEvent) {
         if (!open) return;
         if (wrapRef.current && !wrapRef.current.contains(e.target as Node) && anchorRef.current && !anchorRef.current.contains(e.target as Node)) onClose();
      }

      window.addEventListener('mousedown', handle);
      return () => window.removeEventListener('mousedown', handle);
   }, [open, onClose, anchorRef]);

   function toggle(group: keyof FilterState, option: string) {
      const current = value[group] as string[];
      if (current.includes(option)) {
         onChange({ ...value, [group]: [] });
      } else {
         onChange({ ...value, [group]: [option] });
      }
   }

   function handleDateChange(field: 'startDate' | 'endDate', val: string) {
      if (val) {
         onChange({ ...value, [field]: val, ranges: [] });
      } else {
         const next = { ...value, ranges: [] };
         delete next[field];
         onChange(next);
      }
   }

   function handleRangeToggle(group: keyof FilterState, option: string) {
      const current = value[group] as string[];
      if (current.includes(option)) {
         onChange({ ...value, [group]: [] });
      } else {
         const next = { ...value, [group]: [option] };
         delete next.startDate;
         delete next.endDate;
         onChange(next);
      }
   }

   if (!open) return null;

   const presetGroups = groups.filter(g => g.key !== 'ranges');
   const rangeGroup = groups.find(g => g.key === 'ranges')!;

   return (
      <PopWrap ref={wrapRef} role="dialog" aria-label="Filter options">
         <SearchBar>
            <Icon icon="solar:magnifer-linear" width={14} className="text-[#CACACC] mb-0.5" />
            <SearchInput
               placeholder="Search filters"
               value={query}
               onChange={e => setQuery(e.target.value)}
            />
         </SearchBar>

         {presetGroups.map(g => {
            const filtered = g.options.filter(o => o.toLowerCase().includes(query.toLowerCase()));
            return (
               <SectionBlock key={g.key}>
                  <SectionTitle>{g.title}</SectionTitle>
                  {filtered.map(opt => {
                     const active = (value[g.key] as string[]).includes(opt);
                     return (
                        <OptionRow key={opt}>
                           <HiddenCheckbox checked={active} onChange={() => toggle(g.key, opt)} />
                           <Box $checked={active}>{active && <Icon icon="solar:check-linear" width={12} />}</Box>
                           <span className='text-sm'>{opt}</span>
                        </OptionRow>
                     );
                  })}
               </SectionBlock>
            );
         })}

         {'Date Range'.toLowerCase().includes(query.toLowerCase()) && (
            <SectionBlock>
               <SectionTitle>Date Range</SectionTitle>
               {rangeGroup.options.filter(o => o.toLowerCase().includes(query.toLowerCase())).map(opt => {
                  const active = value.ranges.includes(opt);
                  return (
                     <OptionRow key={opt}>
                        <HiddenCheckbox checked={active} onChange={() => handleRangeToggle('ranges', opt)} />
                        <Box $checked={active}>{active && <Icon icon="solar:check-linear" width={12} />}</Box>
                        <span className='text-sm'>{opt}</span>
                     </OptionRow>
                  );
               })}
               <div className="mt-2 flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                     <span className="text-xs text-[#8A8A8E] w-10">From</span>
                     <input
                        type="date"
                        value={value.startDate ?? ''}
                        onChange={e => handleDateChange('startDate', e.target.value)}
                        className="flex-1 text-xs border border-[#E7E7E9] rounded-md px-2 py-1 outline-none focus:border-[#B3B3B6] bg-white"
                     />
                  </div>
                  <div className="flex items-center gap-2">
                     <span className="text-xs text-[#8A8A8E] w-10">To</span>
                     <input
                        type="date"
                        value={value.endDate ?? ''}
                        onChange={e => handleDateChange('endDate', e.target.value)}
                        className="flex-1 text-xs border border-[#E7E7E9] rounded-md px-2 py-1 outline-none focus:border-[#B3B3B6] bg-white"
                     />
                  </div>
               </div>
            </SectionBlock>
         )}

         <FooterBar>
            <Icon icon="solar:checklist-linear" width={14} />
            <span>
               {(() => {
                  const count = value.types.length + value.statuses.length + value.ranges.length + (value.startDate ? 1 : 0) + (value.endDate ? 1 : 0);
                  return count === 0 ? 'Select categories' : `${count} filter${count === 1 ? '' : 's'} selected`;
               })()}
            </span>
         </FooterBar>
      </PopWrap>
   );
};

export default FilterDropdown;
